import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  GetRecoveryOpportunitiesAPI,
  GetRecoveryCandidatesAPI,
  ClassifyRecoveryChatsAPI,
  ComposeRecoveryMessageAPI,
  SendRecoveryContactAPI,
  DismissRecoveryCandidateAPI,
  GetRecoveryActionsAPI,
} from '../../../../api/API';
import { inferStepMinutes, runChatClassification } from '../utils';

export const DEFAULT_FILTERS = { days: 7, professionalId: 0, procedureId: 0 };
export const ACTIONS_DAYS = 30;
const GENERIC_ERROR = 'Não foi possível carregar agora. Tente de novo.';

function sameInterval(a, b) {
  return Boolean(a && b) && a.professionalId === b.professionalId && a.date === b.date && a.start === b.start;
}

/**
 * Dados da tela Recuperar atendimentos: horários livres (com filtros), pessoas
 * para chamar do intervalo aberto, envio, "agora não" e o painel de resultado.
 * Cada bloco tem status: loading | ready | error (candidatos começam em idle).
 *
 * Conversa de WhatsApp: a lista chega na hora e, em segundo plano, a IA lê a
 * última conversa de quem ainda não foi lida (`chat.pending`), em lotes. Cada
 * resultado entra na linha; se alguma intenção mudou a lista (quem disse que
 * não vai continuar, quem pediu horário), a lista é recarregada em silêncio,
 * mantendo quem já foi chamada ou dispensada.
 *
 * Calibração (beta): `candidates.rejected` traz quem cabe no intervalo e
 * ficou fora da rodada, com nota e motivo; as conversas dessas pessoas também
 * são lidas, para conferir a interpretação.
 */
function useRecoveryData() {
  // Cancela a leitura das conversas quando o intervalo muda ou fecha
  const cancelClassification = useRef(null);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [opportunities, setOpportunities] = useState({
    status: 'loading', days: [], professionals: [], procedures: [], error: null,
  });
  const [candidates, setCandidates] = useState({ status: 'idle', interval: null, list: [], rejected: null, error: null });
  const [actions, setActions] = useState({
    status: 'loading', summary: null, attributionRule: '', list: [], error: null,
  });

  const loadOpportunities = useCallback(() => {
    setOpportunities((prev) => ({ ...prev, status: 'loading', error: null }));
    GetRecoveryOpportunitiesAPI(filters.days, filters.professionalId, filters.procedureId, (response) => {
      if (!response?.success || !response.data) {
        setOpportunities((prev) => ({ ...prev, status: 'error', error: response?.error || GENERIC_ERROR }));
        return;
      }
      setOpportunities({
        status: 'ready',
        days: response.data.days || [],
        professionals: response.data.professionals || [],
        procedures: response.data.procedures || [],
        error: null,
      });
    });
  }, [filters]);

  const loadActions = useCallback(() => {
    setActions((prev) => ({ ...prev, status: 'loading', error: null }));
    GetRecoveryActionsAPI(ACTIONS_DAYS, (response) => {
      if (!response?.success || !response.data) {
        setActions((prev) => ({ ...prev, status: 'error', error: response?.error || GENERIC_ERROR }));
        return;
      }
      setActions({
        status: 'ready',
        summary: response.data.summary || null,
        attributionRule: response.data.attributionRule || '',
        list: response.data.actions || [],
        error: null,
      });
    });
  }, []);

  useEffect(() => { loadOpportunities(); }, [loadOpportunities]);
  useEffect(() => { loadActions(); }, [loadActions]);

  const patchCandidate = useCallback((patientId, patch) => {
    const apply = (c) => (c.patientId === patientId ? { ...c, ...patch } : c);
    setCandidates((prev) => ({
      ...prev,
      list: prev.list.map(apply),
      rejected: prev.rejected ? { ...prev.rejected, list: prev.rejected.list.map(apply) } : prev.rejected,
    }));
  }, []);

  /**
   * Recarrega a lista do intervalo aberto sem estado de carregamento, depois que
   * a leitura das conversas mudou alguém. Mantém "Chamada", "agora não" e o
   * sinal já lido de quem o servidor ainda marcaria como pendente.
   */
  const refreshCandidates = useCallback((interval) => {
    GetRecoveryCandidatesAPI(interval, (response) => {
      if (!response?.success || !response.data) return;
      setCandidates((prev) => {
        if (prev.status !== 'ready' || !sameInterval(prev.interval, interval)) return prev;
        const previous = new Map(prev.list.map((c) => [c.patientId, c]));
        const list = (response.data.candidates || []).map((fresh) => {
          const old = previous.get(fresh.patientId);
          if (!old) return fresh;
          const keepChannel = old.called || old.dismissed;
          return {
            ...fresh,
            called: old.called,
            dismissed: old.dismissed,
            channel: keepChannel ? old.channel : fresh.channel,
            chat: fresh.chat?.pending && old.chat && !old.chat.pending ? old.chat : fresh.chat,
          };
        });
        return { ...prev, list, rejected: response.data.rejected || prev.rejected };
      });
    });
  }, []);

  const startClassification = useCallback((interval, list) => {
    if (cancelClassification.current) cancelClassification.current();
    cancelClassification.current = runChatClassification(
      list,
      (ids, callback) => ClassifyRecoveryChatsAPI(ids, callback),
      (patientId, chat) => patchCandidate(patientId, { chat }),
      (changed) => {
        cancelClassification.current = null;
        if (changed) refreshCandidates(interval);
      }
    );
  }, [patchCandidate, refreshCandidates]);

  const loadCandidates = useCallback((interval) => {
    if (cancelClassification.current) {
      cancelClassification.current();
      cancelClassification.current = null;
    }
    if (!interval) {
      setCandidates({ status: 'idle', interval: null, list: [], rejected: null, error: null });
      return;
    }
    setCandidates({ status: 'loading', interval, list: [], rejected: null, error: null });
    GetRecoveryCandidatesAPI(interval, (response) => {
      if (!response?.success || !response.data) {
        setCandidates({ status: 'error', interval, list: [], rejected: null, error: response?.error || GENERIC_ERROR });
        return;
      }
      const list = response.data.candidates || [];
      const rejected = response.data.rejected || null;
      setCandidates({
        status: 'ready',
        interval: response.data.interval || interval,
        list,
        rejected,
        error: null,
      });
      startClassification(response.data.interval || interval, [...list, ...(rejected?.list || [])]);
    });
  }, [startClassification]);

  useEffect(() => () => {
    if (cancelClassification.current) cancelClassification.current();
  }, []);

  /**
   * Texto do convite para uma pessoa e um horário: a IA escreve a partir das
   * razões (source 'ai') ou, se não responder, vem o modelo ('template').
   * O callback recebe { message, source } ou null quando a chamada falha.
   */
  const composeMessage = useCallback((payload, onDone) => {
    ComposeRecoveryMessageAPI(payload, (response) => {
      onDone(response?.success && response.data?.message ? response.data : null);
    });
  }, []);

  /**
   * Envia o convite. Sucesso: a pessoa vira "Chamada" na lista e o painel de
   * resultado recarrega. SLOT_TAKEN: recarrega os horários livres. O callback
   * recebe a resposta crua para a tela decidir a mensagem.
   */
  const sendContact = useCallback((payload, onDone) => {
    SendRecoveryContactAPI(payload, (response) => {
      if (response?.success) {
        patchCandidate(payload.patientId, {
          called: true,
          channel: { status: 'recent_contact', text: 'Chamada agora' },
        });
        loadActions();
      } else if (response?.code === 'SLOT_TAKEN') {
        loadOpportunities();
      }
      onDone(response);
    });
  }, [patchCandidate, loadActions, loadOpportunities]);

  /** "Agora não": a pessoa não é sugerida por 30 dias. */
  const dismissCandidate = useCallback((candidate, onDone) => {
    DismissRecoveryCandidateAPI({ patientId: candidate.patientId, scope: 'patient' }, (response) => {
      if (response?.success) {
        patchCandidate(candidate.patientId, {
          dismissed: true,
          channel: { status: 'dismissed', text: 'Não sugerir por 30 dias' },
        });
      }
      onDone(response);
    });
  }, [patchCandidate]);

  const stepMinutes = useMemo(() => inferStepMinutes(opportunities.days), [opportunities.days]);

  return {
    filters,
    setFilters,
    opportunities,
    loadOpportunities,
    candidates,
    loadCandidates,
    composeMessage,
    sendContact,
    dismissCandidate,
    actions,
    loadActions,
    stepMinutes,
  };
}

export default useRecoveryData;
